import { useState, useEffect } from 'react';
import {
  useDiagnosticRoom,
  useCreateDiagnosticRoom,
  useUpdateDiagnosticRoom,
  useAllFloors,
} from '../../services/httpServices/queries';
import {
  Loader2,
  AlertCircle,
  Plus,
  Save,
  X,
  DoorOpen,
} from 'lucide-react';

interface DiagnosticRoomFormProps {
  isOpen: boolean;
  roomId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function DiagnosticRoomForm({ isOpen, roomId, onClose, onSuccess }: DiagnosticRoomFormProps) {
  const isEdit = !!roomId;

  const { data: roomData, isLoading: roomLoading, error: roomError } = useDiagnosticRoom(roomId);
  const { data: floorsData, isLoading: floorsLoading } = useAllFloors();
  const createMutation = useCreateDiagnosticRoom();
  const updateMutation = useUpdateDiagnosticRoom();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    floorId: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const room = roomData?.data;
  const floorOptions = (floorsData?.data || []).map((f: any) => ({
    value: f.id,
    label: `${f.name} (Floor ${f.floorNumber})`,
  }));

  useEffect(() => {
    if (isEdit && room) {
      setFormData({
        name: room.name,
        description: room.description || '',
        floorId: room.floorId || '',
      });
    } else if (!isEdit) {
      setFormData({ name: '', description: '', floorId: '' });
    }
  }, [isEdit, room]);

  useEffect(() => {
    if (isOpen) {
      setFormErrors({});
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const data: any = {
      name: formData.name,
      description: formData.description || undefined,
    };
    if (formData.floorId) {
      data.floorId = formData.floorId;
    }

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ id: roomId!, data });
      } else {
        await createMutation.mutateAsync(data);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Something went wrong';
      setFormErrors({ general: message });
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const inputBase = 'w-full px-3 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 text-sm transition-all';
  const inputValid = 'border-border focus:ring-primary';
  const inputInvalid = 'border-danger focus:ring-danger';
  const labelClass = 'block text-sm font-medium text-text mb-1';

  const ic = (field: string) => `${inputBase} ${formErrors[field] ? inputInvalid : inputValid}`;
  const err = (field: string) => formErrors[field] ? <p className="text-xs text-danger mt-1">{formErrors[field]}</p> : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-surface rounded-xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-surface z-10 flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <DoorOpen size={18} className="text-primary" />
            <h2 className="text-lg font-semibold text-text">
              {isEdit ? 'Edit Room' : 'Add Room'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background text-text-muted transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {isEdit && roomLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}

          {isEdit && (roomError || !room) && !roomLoading && (
            <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
              <AlertCircle size={18} />
              <span>Failed to load room details. Please try again.</span>
            </div>
          )}

          {(!isEdit || (room && !roomLoading)) && (
            <form id="room-form" onSubmit={handleSubmit} className="space-y-4">
              {formErrors.general && (
                <div className="bg-danger/10 text-danger rounded-lg p-4 flex items-center gap-2">
                  <AlertCircle size={18} />
                  <span className="text-sm font-medium">{formErrors.general}</span>
                </div>
              )}

              <div>
                <label className={labelClass}>Name *</label>
                <input
                  type="text"
                  className={ic('name')}
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Room 201"
                />
                {err('name')}
              </div>

              <div>
                <label className={labelClass}>Floor</label>
                <select
                  className={`${inputBase} ${inputValid}`}
                  value={formData.floorId}
                  onChange={(e) => updateField('floorId', e.target.value)}
                >
                  <option value="">Select Floor (Optional)</option>
                  {floorOptions.map((opt: any) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {floorsLoading && (
                  <p className="text-xs text-text-muted mt-1 flex items-center gap-1">
                    <Loader2 size={12} className="animate-spin" /> Loading floors...
                  </p>
                )}
              </div>

              <div>
                <label className={labelClass}>Description</label>
                <textarea
                  className={`${inputBase} ${inputValid}`}
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={3}
                  placeholder="Optional description of the room..."
                />
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-surface z-10 flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-border rounded-lg hover:bg-background transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="room-form"
            disabled={createMutation.isPending || updateMutation.isPending || (isEdit && roomLoading)}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2 shadow-sm"
          >
            {(createMutation.isPending || updateMutation.isPending) && <Loader2 size={16} className="animate-spin" />}
            {isEdit ? <Save size={16} /> : <Plus size={16} />}
            {isEdit ? 'Save Changes' : 'Add Room'}
          </button>
        </div>
      </div>
    </div>
  );
}
